import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Icons, PRODUCT_STATUSES, FLIGHT_STATUSES, newFlightLeg, resolveSupplierCommercialRole, findSupplierService } from "./bookingConstants";
import { useSuppliersList } from "../../api/suppliers/useSuppliersApi";
import { useCheckDuplicateReference, useLookupFlightBaggage } from "../../api/booking/useBookingApi";
import { useToast } from "../../context/ToastContext";
import { useGetAirlines } from "../../api/products/useAirlinesApi";
import { useGetAirports } from "../../api/geo/useAirports";
import { useGetCabinClasses } from "../../api/products/useCabinClassesApi";
import { useGetFareClasses } from "../../api/products/useFareClassesApi";
import s from "./FlightForm.module.css";

/* ─── Searchable Select ─────────────────────────────────────── */

const SearchSelect = ({ value, options, onChange, onSearch, placeholder = "Search...", renderOption, renderSelected, onBlur, invalid, initialOption }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Remember the last option the user actually picked so the pill survives
  // when the options list is re-fetched without our value (e.g. searching
  // "brussels", picking, then the empty-search refetch returning the first
  // 500 airlines that don't include Brussels Airlines).
  const [pickedOption, setPickedOption] = useState(
    initialOption && initialOption.value && initialOption.value === value ? initialOption : null
  );
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        if (open && onBlur) onBlur();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onBlur]);

  useEffect(() => {
    if (!onSearch) return;
    const timer = setTimeout(() => onSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, onSearch]);

  // Drop the cached pick when the parent changes the value behind our back
  // (autofill, clear, edit elsewhere). We never want to show a stale label.
  useEffect(() => {
    if (pickedOption && pickedOption.value !== value) setPickedOption(null);
  }, [value, pickedOption]);

  const filtered = useMemo(() => {
    if (onSearch) return options;
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => (o._search || "").toLowerCase().includes(q));
  }, [options, query, onSearch]);

  const handleSelect = (opt) => {
    setPickedOption(opt);
    onChange(opt.value);
    setOpen(false);
    setQuery("");
    if (onBlur) onBlur();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setPickedOption(null);
    onChange("");
    setQuery("");
    if (onBlur) onBlur();
  };

  const fromOptions = value ? options.find((o) => o.value === value) : null;
  const selected = fromOptions
    || (pickedOption && pickedOption.value === value ? pickedOption : null)
    || (initialOption && initialOption.value === value ? initialOption : null);

  if (selected && !open) {
    return (
      <div className={s.searchSelect} ref={ref}>
        <div className={`${s.searchSelectSelected} ${invalid ? s.inputError : ""}`} onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}>
          {renderSelected ? renderSelected(selected) : (
            <>
              <span className={s.searchSelectSelectedCode}>{selected.code}</span>
              <span className={s.searchSelectSelectedName}>{selected.label}</span>
            </>
          )}
          <button type="button" className={s.searchSelectClear} onClick={handleClear}>×</button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.searchSelect} ref={ref}>
      <input
        ref={inputRef}
        className={`${s.searchSelectInput} ${invalid ? s.inputError : ""}`}
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && (
        <div className={s.searchSelectDropdown}>
          {filtered.length === 0 ? (
            <div className={s.searchSelectEmpty}>No results found</div>
          ) : (
            filtered.slice(0, 50).map((opt) => (
              <div
                key={opt.value}
                className={`${s.searchSelectOption} ${opt.value === value ? s.searchSelectOptionActive : ""}`}
                onClick={() => handleSelect(opt)}
              >
                {renderOption ? renderOption(opt) : (
                  <>
                    <span className={s.searchSelectCode}>{opt.code}</span>
                    <span className={s.searchSelectName}>{opt.label}</span>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};


const calcDayDiff = (depDate, arrDate) => {
  if (!depDate || !arrDate) return null;
  return Math.round((new Date(arrDate) - new Date(depDate)) / (1000 * 60 * 60 * 24));
};

const DayDiffBadge = ({ depDate, arrDate }) => {
  const diff = calcDayDiff(depDate, arrDate);
  if (diff === null) return null;
  const label = diff > 0 ? `+${diff}d` : diff < 0 ? `${diff}d` : "Same day";
  const cls = diff > 0 ? s.dayBadgePlus : diff < 0 ? s.dayBadgeMinus : s.dayBadgeZero;
  // Wrapped in a field column with a hidden-label spacer so the badge self-aligns
  // to the input row (and stays put when Arrival Date/Time shows a validation error).
  return (
    <div className={s.field} style={{ flex: "0 0 auto" }}>
      <label className={s.fieldLabel} aria-hidden="true" style={{ visibility: "hidden" }}>Day</label>
      <span className={`${s.dayBadge} ${cls}`}>{label}</span>
    </div>
  );
};

const isPnrInvalid = (val) => val && val.length > 0 && val.length < 5;

const PnrInput = ({ value, onChange, placeholder = "e.g. ABC123", label = "Airline PNR", required }) => {
  const invalid = isPnrInvalid(value);
  const missing = required && !value?.trim();
  return (
    <div className={s.field}>
      <label className={s.fieldLabel}>{label} {required && <span style={{ color: "#dc3545" }}>*</span>}</label>
      <input
        className={`${s.input} ${invalid || missing ? s.inputError : ""}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ textTransform: "uppercase" }}
        minLength={5}
      />
      {invalid && <span className={s.fieldError}>PNR must be at least 5 characters</span>}
      {!invalid && missing && <span className={s.fieldError}>PNR is required</span>}
    </div>
  );
};

/* ─── Validation ───────────────────────────────────────────── */

const AIRLINE_CODE_RE = /^[A-Z0-9]{2,3}$/;
const FLIGHT_NUMBER_RE = /^[A-Z0-9]{1,5}$/;

const BAGGAGE_TYPES = [
  { label: "Personal Item", incKey: "baggageHandbag", kgKey: "baggageHandbagKg", pcKey: "baggageHandbagPc" },
  { label: "Cabin Bag",   incKey: "baggageCabin",   kgKey: "baggageCabinKg",   pcKey: "baggageCabinPc" },
  { label: "Checked Bag", incKey: "baggageChecked", kgKey: "baggageCheckedKg", pcKey: "baggageCheckedPc" },
  { label: "Checked Bag Infant", incKey: "baggageCheckedInfant", kgKey: "baggageCheckedInfantKg", pcKey: "baggageCheckedInfantPc" },
];

// True when any leg has a baggage type marked included (YES) but with no
// allowance (0 kg AND 0 pieces). Used to block saving the flight product.
export const hasInvalidBaggage = (legs) =>
  (legs || []).some((leg) =>
    BAGGAGE_TYPES.some((bag) => {
      if (!leg[bag.incKey]) return false;
      const kg = parseFloat(leg[bag.kgKey]) || 0;
      const pc = parseInt(leg[bag.pcKey], 10) || 0;
      return kg <= 0 && pc <= 0;
    })
  );

export const hasInvalidLegs = (legs) =>
  (legs || []).some((leg) => Object.keys(validateLeg(leg)).length > 0);

/**
 * Why Save is disabled, per leg, in words.
 *
 * Field errors only render once a field is TOUCHED, which never happens on an
 * edit form the user has not typed in. So an existing leg missing (say) an
 * airline name disabled Save with nothing on screen explaining it — the button
 * simply did not work. This turns that dead end into a list of what to fix.
 */
export const legBlockingReasons = (legs) => {
  const LABELS = {
    flightStatus: 'leg status', departure: 'departure airport', arrival: 'arrival airport',
    date: 'departure date', time: 'departure time', arrivalDate: 'arrival date',
    arrivalTime: 'arrival time', airlineName: 'airline name', airlineCode: 'airline code',
    flightNumber: 'flight code', cabinClass: 'cabin class',
  };
  const out = [];
  (legs || []).forEach((leg, i) => {
    const errs = validateLeg(leg);
    const keys = Object.keys(errs);
    if (!keys.length) return;
    const route = [leg.departure, leg.arrival].filter(Boolean).join('→') || `leg ${i + 1}`;
    const named = keys.map((k) => LABELS[k] || errs[k]);
    out.push(`${leg.direction || 'Leg'} ${route}: ${[...new Set(named)].join(', ')}`);
  });
  return out;
};

export const hasMissingPnr = (form) => {
  const isSplit = form.flightPricingMode === "SPLIT";
  if (isSplit) {
    const legs = form.legs || [];
    const hasOutbound = legs.some(l => l.direction === "Outbound");
    const hasInbound = legs.some(l => l.direction === "Inbound");
    if (hasOutbound && !form.outboundPnr?.trim()) return true;
    if (hasInbound && !form.inboundPnr?.trim()) return true;
    return false;
  }
  if (!form.airlinePnr?.trim()) return true;
  return false;
};

// Sentinel for the fare class a leg already carries but the master list does
// not offer — see savedFareUnlisted in LegBlock.
const SAVED_FARE = "__saved__";

const validateLeg = (leg) => {
  const e = {};
  if (!leg.flightStatus) e.flightStatus = "Leg status is required";
  if (!leg.departure) e.departure = "Departure airport is required";
  if (!leg.arrival) e.arrival = "Arrival airport is required";
  if (leg.departure && leg.arrival && leg.departure === leg.arrival) {
    e.arrival = "Arrival must differ from departure";
  }
  if (!leg.date) e.date = "Departure date is required";
  if (!leg.time) e.time = "Departure time is required";
  if (!leg.arrivalDate) e.arrivalDate = "Arrival date is required";
  if (!leg.arrivalTime) e.arrivalTime = "Arrival time is required";
  if (leg.date && leg.arrivalDate && leg.arrivalDate < leg.date) {
    e.arrivalDate = "Arrival cannot be before departure";
  }
  if (
    leg.date && leg.time && leg.arrivalDate && leg.arrivalTime &&
    leg.arrivalDate === leg.date && leg.arrivalTime <= leg.time
  ) {
    e.arrivalTime = "Arrival time must be after departure";
  }
  if (!leg.airlineName) e.airlineName = "Airline name is required";
  if (!leg.airlineCode) e.airlineCode = "Airline code is required";
  else if (!AIRLINE_CODE_RE.test(leg.airlineCode)) e.airlineCode = "2–3 letters/digits";
  if (!leg.flightNumber) e.flightNumber = "Flight code is required";
  else if (!FLIGHT_NUMBER_RE.test(leg.flightNumber)) e.flightNumber = "1–5 letters/digits";
  if (!leg.cabinClass) e.cabinClass = "Cabin class is required";
  // Fare class is optional.
  // Baggage: if a baggage type is marked included (YES) it must carry a non-zero
  // allowance — either kg or pieces. "Included but 0/0" is invalid.
  BAGGAGE_TYPES.forEach((bag) => {
    if (leg[bag.incKey]) {
      const kg = parseFloat(leg[bag.kgKey]) || 0;
      const pc = parseInt(leg[bag.pcKey], 10) || 0;
      if (kg <= 0 && pc <= 0) {
        e[`baggage_${bag.incKey}`] = `${bag.label}: enter kg or pieces (cannot be 0)`;
      }
    }
  });
  return e;
};

/* ─── Single Leg ───────────────────────────────────────────── */

const LegBlock = ({ leg, legIdx, direction, onUpdate, onCopyBaggage, onLookupBaggage, lookingUpBaggage, totalLegs, autofillTarget, airlineOptions, airportOptions, cabinClassOptions, fareClassOptions, onAirlineSearch, onAirportSearch }) => {
  const dirColor = direction === "Outbound" ? s.tagOutbound : s.tagInbound;
  const [touched, setTouched] = useState({});
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const copyMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (copyMenuRef.current && !copyMenuRef.current.contains(e.target)) setCopyMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const errors = validateLeg(leg);
  const touch = (key) => setTouched((t) => (t[key] ? t : { ...t, [key]: true }));
  const showErr = (key) => touched[key] && errors[key];
  const cls = (key) => showErr(key) ? s.inputError : "";

  // For RT Full Flight inbound autofill, mark override flags when the user
  // manually changes departure / arrival / airline so the autofill rule
  // stops overwriting that field.
  const updateInboundManual = (overrideKey, updates) => {
    if (autofillTarget && !leg[overrideKey]) {
      onUpdate(leg.id, { ...updates, [overrideKey]: true });
    } else {
      onUpdate(leg.id, updates);
    }
  };

  /**
   * Fill in an airline name the leg is missing but that its code already
   * implies.
   *
   * Airtuerk-imported legs arrive with an airlineCode and an EMPTY airlineName —
   * 51 legs on production. Airline name is a required field, and its error only
   * renders once the field is touched, so opening one of those legs disabled
   * Save with nothing on screen to explain it. The code already identifies the
   * airline; making someone retype the name it maps to is busywork.
   *
   * Only ever fills a blank. A name that disagrees with the code is left alone —
   * that is a data question, not something to silently overwrite.
   */
  useEffect(() => {
    if (!leg.airlineCode || leg.airlineName) return;
    const match = airlineOptions.find((a) => a.value === leg.airlineCode);
    if (match?.label) onUpdate(leg.id, { airlineName: match.label });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leg.airlineCode, leg.airlineName, airlineOptions]);

  const handleAirlineChange = (code) => {
    const match = airlineOptions.find((a) => a.value === code);
    const upd = { airlineCode: code, airlineName: match ? match.label : "", fareClass: "", fareClassName: "", fareClassId: null };
    if (autofillTarget) updateInboundManual("_overAirline", upd);
    else onUpdate(leg.id, upd);
  };

  const handleAirlineCodeChange = (val) => {
    const code = val.toUpperCase();
    const match = airlineOptions.find((a) => a.value === code);
    const upd = { airlineCode: code, airlineName: match ? match.label : "", fareClass: "", fareClassName: "", fareClassId: null };
    if (autofillTarget) updateInboundManual("_overAirline", upd);
    else onUpdate(leg.id, upd);
  };

  const handleDepartureChange = (val) => {
    const opt = airportOptions.find((o) => o.value === val);
    const departureName = opt ? opt.city : val;
    if (autofillTarget) updateInboundManual("_overDeparture", { departure: val, departureName });
    else onUpdate(leg.id, { departure: val, departureName });
  };

  const handleArrivalChange = (val) => {
    const opt = airportOptions.find((o) => o.value === val);
    const arrivalName = opt ? opt.city : val;
    if (autofillTarget) updateInboundManual("_overArrival", { arrival: val, arrivalName });
    else onUpdate(leg.id, { arrival: val, arrivalName });
  };

  const relevantFareClasses = useMemo(() => {
    if (!fareClassOptions || fareClassOptions.length === 0) return [];
    if (!leg.airlineCode || !leg.cabinClass) return [];

    const norm = (v) => String(v || "").trim().toLowerCase();
    const legAirline = norm(leg.airlineCode);
    const legCabin = norm(leg.cabinClass);

    return fareClassOptions.filter(
      (fc) => norm(fc.airlineCode) === legAirline && norm(fc.cabinClassName) === legCabin
    );
  }, [fareClassOptions, leg.airlineCode, leg.cabinClass]);

  // When form opens with existing data that has a fare class but no fareClassId,
  // auto-resolve it once relevantFareClasses are loaded. Either half identifies
  // the row: a booking made here stores the code, one that came back from a
  // supplier often carries only the name.
  useEffect(() => {
    if (leg.fareClassId || relevantFareClasses.length === 0) return;
    const norm = (v) => String(v || "").trim().toLowerCase();
    const match = relevantFareClasses.find(
      (fc) =>
        (leg.fareClass && norm(fc.fareClassCode) === norm(leg.fareClass)) ||
        (leg.fareClassName && norm(fc.fareClassName) === norm(leg.fareClassName))
    );
    if (match) onUpdate(leg.id, { fareClassId: match.id, fareClassName: match.fareClassName });
  }, [relevantFareClasses]); // eslint-disable-line react-hooks/exhaustive-deps

  // A leg can carry a fare class the master list does not offer: an imported
  // booking stores the airline's own booking class ("Y", "T", "1"), while Fare
  // Classes are keyed by the airline's marketed fares. Leaving the field blank
  // there reads as "this flight has no fare class" when the booking plainly
  // records one, so the saved value is offered as an option in its own right —
  // selectable, and replaced the moment a real fare class is picked.
  const savedFareLabel = [leg.fareClass, leg.fareClassName]
    .filter(Boolean)
    .map((v) => String(v).toUpperCase())
    .join(" — ");
  const savedFareUnlisted = !leg.fareClassId && !!savedFareLabel;

  const handleCopy = (mode) => {
    onCopyBaggage(leg.id, mode);
    setCopyMenuOpen(false);
  };

  const isLastLeg = legIdx === totalLegs - 1;

  return (
    <div className={`${s.legCard} ${direction === "Outbound" ? s.legCardOutbound : s.legCardInbound}`}>
      <div className={s.legHeader}>
        <div className={s.legHeaderLeft}>
          <span className={`${s.dirTag} ${dirColor}`}>
            {direction === "Outbound" ? (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            ) : (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            )}
            {direction}
          </span>
          <span className={s.legNum}>Leg {legIdx + 1}</span>
        </div>
      </div>

      <div className={s.mfRow2}>
        <div className={s.field}>
          <label className={s.fieldLabel}>Leg Status <span className={s.req}>*</span></label>
          <select
            className={`${s.input} ${cls("flightStatus")}`}
            value={leg.flightStatus || ""}
            onChange={(e) => onUpdate(leg.id, { flightStatus: e.target.value })}
            onBlur={() => touch("flightStatus")}
          >
            <option value="">Select status</option>
            {/* A leg can carry a status this list does not offer — "Scheduled"
                (which the edit mapper itself used to default to) or "OK" (what
                Airtuerk sends). A controlled select whose value matches no
                option renders BLANK, so the field looked unfilled on every such
                leg. Offer the saved value so it displays and round-trips
                untouched. Same rule as the fare class below. */}
            {leg.flightStatus && !FLIGHT_STATUSES.includes(leg.flightStatus) && (
              <option value={leg.flightStatus}>{leg.flightStatus} (saved)</option>
            )}
            {FLIGHT_STATUSES.map((st) => <option key={st}>{st}</option>)}
          </select>
          {showErr("flightStatus") && <span className={s.fieldError}>{errors.flightStatus}</span>}
        </div>
        <div className={s.field} />
      </div>

      <div className={s.routeRow}>
        <div className={s.field}>
          <label className={s.fieldLabel}>Departure Airport <span className={s.req}>*</span></label>
          <SearchSelect
            value={leg.departure}
            options={airportOptions}
            onChange={handleDepartureChange}
            onSearch={onAirportSearch}
            placeholder="Search airport..."
            invalid={!!showErr("departure")}
            onBlur={() => touch("departure")}
            initialOption={leg.departure ? { value: leg.departure, code: leg.departure, label: leg.departureName || leg.departure } : null}
          />
          {showErr("departure") && <span className={s.fieldError}>{errors.departure}</span>}
        </div>
        <div className={s.routeArrow}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg></div>
        <div className={s.field}>
          <label className={s.fieldLabel}>Arrival Airport <span className={s.req}>*</span></label>
          <SearchSelect
            value={leg.arrival}
            options={airportOptions}
            onChange={handleArrivalChange}
            onSearch={onAirportSearch}
            placeholder="Search airport..."
            invalid={!!showErr("arrival")}
            onBlur={() => touch("arrival")}
            initialOption={leg.arrival ? { value: leg.arrival, code: leg.arrival, label: leg.arrivalName || leg.arrival } : null}
          />
          {showErr("arrival") && <span className={s.fieldError}>{errors.arrival}</span>}
        </div>
      </div>

      <div className={s.mfRow2}>
        <div className={s.field}>
          <label className={s.fieldLabel}>Departure Date <span className={s.req}>*</span></label>
          <input
            className={`${s.input} ${cls("date")}`}
            type="date"
            value={leg.date}
            onChange={(e) => {
              const depDate = e.target.value;
              const updates = { date: depDate, arrivalDate: depDate };
              if (depDate && leg.arrivalTime && leg.time && leg.arrivalTime < leg.time) {
                const next = new Date(depDate);
                next.setDate(next.getDate() + 1);
                updates.arrivalDate = next.toISOString().slice(0, 10);
              }
              onUpdate(leg.id, updates);
            }}
            onBlur={() => touch("date")}
          />
          {showErr("date") && <span className={s.fieldError}>{errors.date}</span>}
        </div>
        <div className={s.field}>
          <label className={s.fieldLabel}>Departure Time <span className={s.req}>*</span></label>
          <input
            className={`${s.input} ${cls("time")}`}
            type="time"
            value={leg.time}
            onChange={(e) => {
              const depTime = e.target.value;
              const updates = { time: depTime };
              if (leg.date && leg.arrivalTime && depTime) {
                if (leg.arrivalTime < depTime) {
                  const next = new Date(leg.date);
                  next.setDate(next.getDate() + 1);
                  updates.arrivalDate = next.toISOString().slice(0, 10);
                } else {
                  updates.arrivalDate = leg.date;
                }
              }
              onUpdate(leg.id, updates);
            }}
            onBlur={() => touch("time")}
          />
          {showErr("time") && <span className={s.fieldError}>{errors.time}</span>}
        </div>
      </div>

      <div className={s.legArrivalRow}>
        <div className={s.mfRow2} style={{ flex: 1 }}>
          <div className={s.field}>
            <label className={s.fieldLabel}>Arrival Date <span className={s.req}>*</span></label>
            <input
              className={`${s.input} ${cls("arrivalDate")}`}
              type="date"
              value={leg.arrivalDate}
              onChange={(e) => onUpdate(leg.id, { arrivalDate: e.target.value })}
              onBlur={() => touch("arrivalDate")}
            />
            {showErr("arrivalDate") && <span className={s.fieldError}>{errors.arrivalDate}</span>}
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Arrival Time <span className={s.req}>*</span></label>
            <input
              className={`${s.input} ${cls("arrivalTime")}`}
              type="time"
              value={leg.arrivalTime}
              onChange={(e) => {
                const arrTime = e.target.value;
                const updates = { arrivalTime: arrTime };
                if (leg.date && leg.time && arrTime) {
                  if (arrTime < leg.time) {
                    const next = new Date(leg.date);
                    next.setDate(next.getDate() + 1);
                    updates.arrivalDate = next.toISOString().slice(0, 10);
                  } else {
                    updates.arrivalDate = leg.date;
                  }
                }
                onUpdate(leg.id, updates);
              }}
              onBlur={() => touch("arrivalTime")}
            />
            {showErr("arrivalTime") && <span className={s.fieldError}>{errors.arrivalTime}</span>}
          </div>
        </div>
        <DayDiffBadge depDate={leg.date} arrDate={leg.arrivalDate} />
      </div>

      <div className={s.airlineRow}>
        <div className={s.field}>
          <label className={s.fieldLabel}>Airline Name <span className={s.req}>*</span></label>
          <SearchSelect
            value={leg.airlineCode || ""}
            options={airlineOptions}
            onChange={handleAirlineChange}
            onSearch={onAirlineSearch}
            placeholder="Search airline..."
            invalid={!!showErr("airlineName")}
            onBlur={() => touch("airlineName")}
            initialOption={leg.airlineCode ? { value: leg.airlineCode, code: leg.airlineCode, label: leg.airlineName || leg.airlineCode } : null}
          />
          {showErr("airlineName") && <span className={s.fieldError}>{errors.airlineName}</span>}
        </div>
        <div className={s.field}>
          <label className={s.fieldLabel}>Airline Code <span className={s.req}>*</span></label>
          <input
            className={`${s.input} ${cls("airlineCode")}`}
            placeholder="e.g. TK"
            maxLength={3}
            value={leg.airlineCode || ""}
            onChange={(e) => handleAirlineCodeChange(e.target.value)}
            onBlur={() => touch("airlineCode")}
            style={{ textTransform: "uppercase" }}
          />
          {showErr("airlineCode") && <span className={s.fieldError}>{errors.airlineCode}</span>}
        </div>
        <div className={s.field}>
          <label className={s.fieldLabel}>Flight Code <span className={s.req}>*</span></label>
          <input
            className={`${s.input} ${cls("flightNumber")}`}
            placeholder="e.g. 1234"
            value={leg.flightNumber || ""}
            onChange={(e) => onUpdate(leg.id, { flightNumber: e.target.value.toUpperCase() })}
            onBlur={() => touch("flightNumber")}
            style={{ textTransform: "uppercase" }}
          />
          {showErr("flightNumber") && <span className={s.fieldError}>{errors.flightNumber}</span>}
        </div>
      </div>

      <div className={s.mfRow2}>
        <div className={s.field}>
          <label className={s.fieldLabel}>Cabin Class <span className={s.req}>*</span></label>
          <select
            className={`${s.input} ${cls("cabinClass")}`}
            value={leg.cabinClass || ""}
            onChange={(e) => onUpdate(leg.id, { cabinClass: e.target.value, fareClass: "", fareClassName: "", fareClassId: null })}
            onBlur={() => touch("cabinClass")}
            style={{ textTransform: "uppercase" }}
          >
            <option value="">Select...</option>
            {cabinClassOptions.map((c) => <option key={c.id} value={c.cabinClassName} style={{ textTransform: "uppercase" }}>{(c.cabinClassName || "").toUpperCase()}</option>)}
          </select>
          {showErr("cabinClass") && <span className={s.fieldError}>{errors.cabinClass}</span>}
        </div>
        <div className={s.field}>
          <label className={s.fieldLabel}>Fare Class</label>
          <select
            className={`${s.input} ${cls("fareClass")}`}
            value={leg.fareClassId ? String(leg.fareClassId) : (savedFareUnlisted ? SAVED_FARE : "")}
            onChange={(e) => {
              const selId = e.target.value;
              if (selId === SAVED_FARE) return; // already what the booking says
              if (!selId) { onUpdate(leg.id, { fareClass: "", fareClassName: "", fareClassId: null }); return; }
              const fc = relevantFareClasses.find(f => String(f.id) === selId);
              onUpdate(leg.id, { fareClass: fc ? fc.fareClassCode : "", fareClassName: fc ? fc.fareClassName : "", fareClassId: fc ? fc.id : null });
            }}
            onBlur={() => touch("fareClass")}
            disabled={!leg.airlineCode || !leg.cabinClass}
            style={{ textTransform: "uppercase" }}
          >
            <option value="">
              {!leg.airlineCode || !leg.cabinClass
                ? "Select airline & cabin first"
                : relevantFareClasses.length === 0
                  ? "No fare class for this airline/cabin"
                  : "Select..."}
            </option>
            {savedFareUnlisted && (
              <option value={SAVED_FARE} style={{ textTransform: "uppercase" }}>{savedFareLabel}</option>
            )}
            {relevantFareClasses.map((fc) => <option key={fc.id} value={String(fc.id)} style={{ textTransform: "uppercase" }}>{(fc.fareClassCode || "").toUpperCase()} — {(fc.fareClassName || "").toUpperCase()}</option>)}
          </select>
          {showErr("fareClass") && <span className={s.fieldError}>{errors.fareClass}</span>}
        </div>
      </div>

      <div className={s.flightBaggageSection}>
        <div className={s.flightBaggageHeader}>
          <span className={s.flightBaggageSectionTitle}>Baggage Allowance</span>
          <div className={s.flightBaggageCopy} ref={copyMenuRef}>
            <button
              type="button"
              className={s.flightBaggageCopyBtn}
              onClick={() => setCopyMenuOpen((o) => !o)}
              title="Copy baggage to other legs"
            >
              {Icons.copy}
              Copy
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            {copyMenuOpen && (
              <div className={s.flightBaggageCopyMenu}>
                <button
                  type="button"
                  className={s.flightBaggageCopyOpt}
                  onClick={() => handleCopy("next")}
                  disabled={isLastLeg}
                  title={isLastLeg ? "No following legs" : "Copy to all following legs"}
                >
                  Copy to next legs
                </button>
                <button
                  type="button"
                  className={s.flightBaggageCopyOpt}
                  onClick={() => handleCopy("all")}
                  disabled={totalLegs <= 1}
                  title={totalLegs <= 1 ? "Only one leg" : "Copy to all legs in this flight"}
                >
                  Copy to all legs
                </button>
              </div>
            )}
          </div>
          {/* Same lookup the import runs: the allowance Airtuerk states for this fare. Runs by
              itself once the flight is identified; the button is for a second opinion. */}
          <button
            type="button"
            className={s.flightBaggageCopyBtn}
            onClick={() => onLookupBaggage?.(leg.id)}
            disabled={lookingUpBaggage}
            title="Fill the baggage allowance from Airtuerk's fare data for this flight"
          >
            {lookingUpBaggage ? "Looking up…" : "From Airtuerk"}
          </button>
        </div>
        {BAGGAGE_TYPES.map((bag) => {
          const bagErr = errors[`baggage_${bag.incKey}`];
          return (
          <div key={bag.incKey}>
            <div className={s.flightBaggageRow}>
              <span className={s.flightBaggageLabel}>{bag.label}</span>
              <div className={s.flightBaggageToggle}>
                <button type="button" className={`${s.flightBaggageBtn} ${leg[bag.incKey] ? s.flightBaggageBtnYes : ""}`} onClick={() => onUpdate(leg.id, { [bag.incKey]: true })}>YES</button>
                <button type="button" className={`${s.flightBaggageBtn} ${!leg[bag.incKey] ? s.flightBaggageBtnNo : ""}`} onClick={() => onUpdate(leg.id, { [bag.incKey]: false, [bag.kgKey]: 0, [bag.pcKey]: 0 })}>NO</button>
              </div>
              <div className={s.flightBaggageInputWrap}>
                <input className={`${s.input} ${s.flightBaggageQtyInput} ${bagErr ? s.inputError : ""}`} type="number" min="0" placeholder="0" value={leg[bag.kgKey] || ""} onChange={(e) => onUpdate(leg.id, { [bag.kgKey]: parseFloat(e.target.value) || 0 })} disabled={!leg[bag.incKey]} />
                <span className={s.flightBaggageUnitInside}>kg</span>
              </div>
              <div className={s.flightBaggageInputWrap}>
                <input className={`${s.input} ${s.flightBaggageQtyInput} ${bagErr ? s.inputError : ""}`} type="number" min="0" placeholder="0" value={leg[bag.pcKey] || ""} onChange={(e) => onUpdate(leg.id, { [bag.pcKey]: parseInt(e.target.value) || 0 })} disabled={!leg[bag.incKey]} />
                <span className={s.flightBaggageUnitInside}>pc</span>
              </div>
            </div>
            {bagErr && <span className={s.fieldError}>{bagErr}</span>}
          </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Direction Group ──────────────────────────────────────── */

const DirectionGroup = ({ direction, title, legs, onUpdateLeg, onRemoveLeg, onAddLeg, onCopyBaggage, onLookupBaggage, lookingUpBaggage, autofillTarget, canAdd, flightType, pnrFields, airlineOptions, airportOptions, cabinClassOptions, fareClassOptions, onAirlineSearch, onAirportSearch, globalTotalLegs }) => {
  const colorClass = direction === "Outbound" ? s.groupOutbound : s.groupInbound;
  const iconCls = direction === "Outbound" ? s.groupIconOutbound : s.groupIconInbound;
  const canRemove = legs.length > 1;

  const handleRemoveLast = () => {
    if (!canRemove) return;
    const last = legs[legs.length - 1];
    onRemoveLeg(last.id);
  };

  return (
    <div className={`${s.directionGroup} ${colorClass}`}>
      <div className={s.directionGroupHeader}>
        <div className={`${s.directionGroupIcon} ${iconCls}`}>
          {direction === "Outbound" ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          )}
        </div>
        <div>
          <div className={s.directionGroupTitle}>{title}</div>
          <div className={s.directionGroupSub}>{legs.length} leg{legs.length !== 1 ? "s" : ""}</div>
        </div>
      </div>

      {pnrFields && (
        <div className={s.dirPnrRow}>
          <PnrInput value={pnrFields.pnr} onChange={(e) => pnrFields.onPnrChange(e.target.value.toUpperCase())} label="Airline PNR" required />
          <div className={s.field}><label className={s.fieldLabel}>Master Ticket Number</label><input className={s.input} placeholder="e.g. 176-2345678900" value={pnrFields.ticket} onChange={(e) => pnrFields.onTicketChange(e.target.value)} /></div>
        </div>
      )}

      <div className={s.legList}>
        {legs.map((leg, idx) => (
          <LegBlock
            key={leg.id}
            leg={leg}
            legIdx={idx}
            totalLegs={globalTotalLegs || legs.length}
            direction={direction}
            onUpdate={onUpdateLeg}
            onCopyBaggage={onCopyBaggage}
            onLookupBaggage={onLookupBaggage}
            lookingUpBaggage={lookingUpBaggage}
            autofillTarget={autofillTarget}
            airlineOptions={airlineOptions}
            airportOptions={airportOptions}
            cabinClassOptions={cabinClassOptions}
            fareClassOptions={fareClassOptions}
            onAirlineSearch={onAirlineSearch}
            onAirportSearch={onAirportSearch}
          />
        ))}
      </div>

      {canAdd && (
        <div className={s.addLegFooter}>
          <button type="button" className={`${s.addSegmentBtn} ${direction === "Inbound" ? s.addSegmentBtnInbound : ""}`} onClick={() => onAddLeg(direction)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Add Leg
          </button>

          {canRemove && (
            <button
              type="button"
              className={s.removeSegmentBtn}
              onClick={handleRemoveLast}
              title={`Remove Leg ${legs.length}`}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Remove Leg
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Duplicate reference warning banner ───────────────────── */
const DuplicateWarn = ({ data, label }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 12px", background: "#fffbeb", border: "1px solid #fbbf24", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
    <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
    <span>
      <strong>Duplicate {label} found</strong> — this reference already exists in{" "}
      {data.bookings.map((b, i) => (
        <span key={b.bookingId}>{i > 0 ? ", " : ""}<strong>{b.bookingReference}</strong> ({b.status})</span>
      ))}.
      You can still continue.
    </span>
  </div>
);

/* ─── Main FlightForm ──────────────────────────────────────── */

const FlightForm = ({ form, f, bookingDate, hasOtherProducts, supplierRefWarn }) => {
  const [airlineSearch, setAirlineSearch] = useState("");
  const [airportSearch, setAirportSearch] = useState("");
  const [toast, setToast] = useState(null);

  // ── PNR duplicate warnings (real-time, debounced 600ms) ────────────────────
  const { execute: checkDuplicate } = useCheckDuplicateReference();
  const [pnrWarn, setPnrWarn] = useState({ airlinePnr: null, outboundPnr: null, inboundPnr: null });

  useEffect(() => {
    const v = form.airlinePnr?.trim();
    if (!v || v.length < 5) { setPnrWarn(w => ({ ...w, airlinePnr: null })); return; }
    const t = setTimeout(async () => {
      const r = await checkDuplicate("airline_pnr", v);
      setPnrWarn(w => ({ ...w, airlinePnr: r.isDuplicate ? r : null }));
    }, 600);
    return () => clearTimeout(t);
  }, [form.airlinePnr]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const v = form.outboundPnr?.trim();
    if (!v || v.length < 5) { setPnrWarn(w => ({ ...w, outboundPnr: null })); return; }
    const t = setTimeout(async () => {
      const r = await checkDuplicate("outbound_pnr", v);
      setPnrWarn(w => ({ ...w, outboundPnr: r.isDuplicate ? r : null }));
    }, 600);
    return () => clearTimeout(t);
  }, [form.outboundPnr]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const v = form.inboundPnr?.trim();
    if (!v || v.length < 5) { setPnrWarn(w => ({ ...w, inboundPnr: null })); return; }
    const t = setTimeout(async () => {
      const r = await checkDuplicate("inbound_pnr", v);
      setPnrWarn(w => ({ ...w, inboundPnr: r.isDuplicate ? r : null }));
    }, 600);
    return () => clearTimeout(t);
  }, [form.inboundPnr]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: suppliers, loading: loadingSuppliers } = useSuppliersList();
  const supplierOptions = useMemo(() =>
    (suppliers || []).filter((sup) => {
      const svc = findSupplierService(sup, "Flight");
      return svc && !!svc.enabled;
    }),
    [suppliers]
  );
  const { data: airlines } = useGetAirlines({ limit: 500, status: 'active', search: airlineSearch });
  // The whole of Geo Data, not the first 500 by name. Sorted alphabetically, 500 stopped
  // at "Bodø" and the field looked like it only knew airports beginning with A or B —
  // everything after that was reachable only by typing, which nobody could guess. There
  // are 4,533 active terminals, and the dropdown renders 50 at a time, so the size costs
  // a load rather than a slow list. Same limit the other two airport fields on the booking
  // screen already use.
  const { data: airportData } = useGetAirports({ limit: 20000, active: true, sortBy: 'name', sortOrder: 'ASC', search: airportSearch });
  const { data: cabinClasses } = useGetCabinClasses({ limit: 100, status: 'active' });
  const { data: fareClasses } = useGetFareClasses({ limit: 500, status: 'active' });

  const airlineOptions = useMemo(() =>
    (airlines || []).map((a) => ({
      value: a.iataCode,
      code: a.iataCode,
      label: a.airlineName,
      _search: `${a.iataCode} ${a.airlineName} ${a.icaoCode || ""} ${a.country || ""}`,
    })),
    [airlines]
  );

  const airportOptions = useMemo(() =>
    (airportData || []).map((a) => {
      const city = a.name.includes(",") ? a.name.split(",")[0].trim() : a.name;
      return {
        value: a.code,
        code: a.code,
        label: a.name,
        city,
        _search: `${a.code} ${a.name} ${a.linkedCities || ""}`,
      };
    }),
    [airportData]
  );

  const cabinClassOptions = useMemo(() => cabinClasses || [], [cabinClasses]);

  const fareClassOptions = useMemo(() =>
    (fareClasses || []).map((fc) => ({
      ...fc,
      airlineCode: fc.airlineCode || "",
      cabinClassName: fc.cabinClass || "",
    })),
    [fareClasses]
  );

  const legs = form.legs || [];

  const updateLeg = (id, updates) =>
    f("legs", prev => (prev || []).map((l) => (l.id === id ? { ...l, ...updates } : l)));

  const removeLeg = (id) => {
    f("legs", prev => {
      const arr = prev || [];
      const target = arr.find((l) => l.id === id);
      if (!target) return arr;
      const sameDirCount = arr.filter((l) => l.direction === target.direction).length;
      if (sameDirCount <= 1) return arr;
      return arr.filter((l) => l.id !== id);
    });
  };

  const addLeg = (direction) => f("legs", prev => [...(prev || []), { ...newFlightLeg(), direction }]);

  // ── Baggage Copy (Universal) ──
  // Copies handbag / cabin / checked baggage values from the source leg to
  // the requested set of target legs. "next" = legs after the source within
  // the entire flight, "all" = every other leg in the flight.
  const copyBaggage = (sourceLegId, mode) => {
    f("legs", prev => {
      const arr = prev || [];
      const idx = arr.findIndex((l) => l.id === sourceLegId);
      if (idx === -1) return arr;
      const src = arr[idx];
      const bag = {
        baggageHandbag: src.baggageHandbag, baggageHandbagKg: src.baggageHandbagKg, baggageHandbagPc: src.baggageHandbagPc,
        baggageCabin: src.baggageCabin, baggageCabinKg: src.baggageCabinKg, baggageCabinPc: src.baggageCabinPc,
        baggageChecked: src.baggageChecked, baggageCheckedKg: src.baggageCheckedKg, baggageCheckedPc: src.baggageCheckedPc,
        baggageCheckedInfant: src.baggageCheckedInfant, baggageCheckedInfantKg: src.baggageCheckedInfantKg, baggageCheckedInfantPc: src.baggageCheckedInfantPc,
      };
      const targetIds = arr
        .filter((l, i) => l.id !== sourceLegId && (mode === "next" ? i > idx : true))
        .map((l) => l.id);
      if (!targetIds.length) return arr;
      return arr.map((l) => (targetIds.includes(l.id) ? { ...l, ...bag } : l));
    });
    setToast("Baggage copied successfully");
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Baggage allowance from Airtuerk ──
  // Neither the staff nor the supplier file know the allowance by heart;
  // Airtuerk's fare data does. Once a leg is identified (airline, flight
  // number, date, route) and its baggage rows are all still NO, the allowance
  // is fetched and filled. A signature per leg stops the same flight being
  // asked twice, and a leg whose baggage was set by hand is never touched —
  // the "From Airtuerk" button is the explicit way to overwrite.
  const { showToast } = useToast();
  const { execute: fetchBaggage, loading: lookingUpBaggage } = useLookupFlightBaggage();
  const lookedUpRef = useRef(new Map()); // leg.id → signature already looked up

  const legSignature = (l) =>
    `${(l.airlineCode || "").toUpperCase()}|${l.flightNumber || ""}|${(l.date || "").slice(0, 10)}|${(l.departure || "").toUpperCase()}|${(l.arrival || "").toUpperCase()}`;
  const legIdentified = (l) => !!(l.airlineCode && l.flightNumber && l.date && l.departure && l.arrival);
  const legHasBaggage = (l) => !!(l.baggageHandbag || l.baggageCabin || l.baggageChecked || l.baggageCheckedInfant);

  const describeAllowance = (b) => {
    const parts = [];
    if (b.baggageChecked) parts.push(`checked ${b.baggageCheckedKg > 0 ? `${b.baggageCheckedKg} kg` : `${b.baggageCheckedPc} pc`}`);
    if (b.baggageCabin) parts.push(`cabin ${b.baggageCabinKg} kg`);
    if (b.baggageCheckedInfant) parts.push(`infant ${b.baggageCheckedInfantKg} kg`);
    return parts.length ? parts.join(", ") : "no checked baggage on this fare";
  };

  /**
   * Look up one or more legs and write the result onto them.
   * `force` (the button) overwrites; the automatic path only fills legs whose
   * baggage rows are all NO.
   */
  const runBaggageLookup = async (targetLegs, { force = false } = {}) => {
    const eligible = targetLegs.filter(legIdentified);
    if (!eligible.length) {
      if (force) showToast("Enter airline, flight number, date, departure and arrival first", "error");
      return;
    }
    eligible.forEach((l) => lookedUpRef.current.set(l.id, legSignature(l)));

    const res = await fetchBaggage(
      eligible.map((l) => ({
        direction: l.direction,
        departure: l.departure,
        arrival: l.arrival,
        date: l.date,
        airlineCode: l.airlineCode,
        flightNumber: l.flightNumber,
        fareClass: l.fareClass,
      }))
    );
    if (!res.success) {
      showToast(res.message || "Baggage lookup on Airtuerk failed", "error");
      return;
    }

    const filled = [];
    const missing = [];
    res.legs.forEach((r) => {
      const leg = eligible[r.index];
      if (!leg) return;
      const label = `${(leg.airlineCode || "").toUpperCase()}${leg.flightNumber}`;
      if (!r.found || !r.baggage) {
        missing.push(r.departed ? `${label} (already departed)` : label);
        return;
      }
      // Automatic fills must not clobber a hand-typed allowance that appeared
      // while the request was in flight.
      f("legs", (prev) =>
        (prev || []).map((l) => {
          if (l.id !== leg.id) return l;
          if (!force && legHasBaggage(l)) return l;
          return { ...l, ...r.baggage };
        })
      );
      filled.push(`${label} ${r.fareName ? `${r.fareName} ` : ""}${describeAllowance(r.baggage)}${r.ambiguous ? " — several fares on this flight, verify" : ""}`);
    });

    if (filled.length) showToast(`Baggage allowance from Airtuerk: ${filled.join("; ")}`, "success");
    if (missing.length) showToast(`Not found on Airtuerk: ${missing.join(", ")} — enter the baggage manually`, "error");
  };

  // The button: one leg, overwrite.
  const lookupBaggageForLeg = (legId) => {
    const leg = (form.legs || []).find((l) => l.id === legId);
    if (leg) runBaggageLookup([leg], { force: true });
  };

  // The automatic path: every newly identified, still-blank leg, debounced so
  // a flight number being typed digit by digit is asked about once.
  const autoLookupSignature = (form.legs || [])
    .map((l) => (legIdentified(l) && !legHasBaggage(l) && lookedUpRef.current.get(l.id) !== legSignature(l) ? `${l.id}:${legSignature(l)}` : ""))
    .filter(Boolean)
    .join(";");

  useEffect(() => {
    if (!autoLookupSignature) return;
    const t = setTimeout(() => {
      const pending = (form.legs || []).filter(
        (l) => legIdentified(l) && !legHasBaggage(l) && lookedUpRef.current.get(l.id) !== legSignature(l)
      );
      if (pending.length) runBaggageLookup(pending);
    }, 900);
    return () => clearTimeout(t);
  }, [autoLookupSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFlightTypeChange = (type) => {
    f("flightType", type);
    f("outboundPnr", "");
    f("outboundTicket", "");
    f("inboundPnr", "");
    f("inboundTicket", "");
    f("airlinePnr", "");
    f("ticketNumber", "");

    if (type === "OW") {
      f("legs", [{ ...newFlightLeg(), direction: "Outbound" }]);
    } else if (type === "RT") {
      f("legs", [{ ...newFlightLeg(), direction: "Outbound" }, { ...newFlightLeg(), direction: "Inbound" }]);
    } else if (type === "Multi") {
      f("legs", [{ ...newFlightLeg(), direction: "Outbound" }, { ...newFlightLeg(), direction: "Inbound" }]);
    }
  };

  const handlePricingModeChange = (mode) => {
    f("flightPricingMode", mode);
    if (mode === "SPLIT" && form.flightType === "OW") handleFlightTypeChange("RT");
  };

  const outboundLegs = legs.filter((l) => l.direction === "Outbound");
  const inboundLegs = legs.filter((l) => l.direction === "Inbound");
  const allLegs = legs;
  const isSplit = form.flightPricingMode === "SPLIT";
  const rtFullAutofill = form.flightType === "RT" && !isSplit;

  // ── RT Full Flight — Inbound Autofill ──
  // When pricing mode is FULL and flight type is RT, the inbound legs derive
  // their departure / arrival airport and airline from the outbound legs.
  // Each inbound field tracks an _over* override flag — once the user edits
  // it manually that flag becomes true and the autofill stops touching it.
  // This effect re-runs whenever the relevant outbound values change.
  const outboundSignature = useMemo(() => (
    outboundLegs.map((l) => `${l.departure}|${l.arrival}|${l.airlineCode}|${l.airlineName}`).join(";")
  ), [outboundLegs]);

  useEffect(() => {
    if (!rtFullAutofill) return;
    if (!outboundLegs.length || !inboundLegs.length) return;
    const firstOut = outboundLegs[0];
    const lastOut = outboundLegs[outboundLegs.length - 1];
    const firstInId = inboundLegs[0].id;
    const lastInId = inboundLegs[inboundLegs.length - 1].id;

    f("legs", prev => {
      const arr = prev || [];
      let changed = false;
      const next = arr.map((l) => {
        if (l.direction !== "Inbound") return l;
        const u = {};
        if (!l._overDeparture && l.id === firstInId && lastOut.arrival && l.departure !== lastOut.arrival) {
          u.departure = lastOut.arrival;
          u.departureName = lastOut.arrivalName || "";
        }
        if (!l._overArrival && l.id === lastInId && firstOut.departure && l.arrival !== firstOut.departure) {
          u.arrival = firstOut.departure;
          u.arrivalName = firstOut.departureName || "";
        }
        if (!l._overAirline && firstOut.airlineCode && (l.airlineCode !== firstOut.airlineCode || l.airlineName !== (firstOut.airlineName || ""))) {
          u.airlineCode = firstOut.airlineCode;
          u.airlineName = firstOut.airlineName || "";
        }
        if (Object.keys(u).length) {
          changed = true;
          return { ...l, ...u };
        }
        return l;
      });
      return changed ? next : arr;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outboundSignature, rtFullAutofill, inboundLegs.length]);

  const handleAirlineSearch = useCallback((q) => setAirlineSearch(q.trim()), []);
  const handleAirportSearch = useCallback((q) => setAirportSearch(q.trim()), []);
  const sharedProps = { airlineOptions, airportOptions, cabinClassOptions, fareClassOptions, onAirlineSearch: handleAirlineSearch, onAirportSearch: handleAirportSearch, onLookupBaggage: lookupBaggageForLeg, lookingUpBaggage };

  // When another product already exists, the flight is implicitly part of the
  // travel booking — lock the booking context to "within_travel".
  useEffect(() => {
    if (hasOtherProducts && form.flightBookingMode !== "within_travel") {
      f("flightBookingMode", "within_travel");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOtherProducts]);

  return (
    <>
        <div className={s.formCard}>
        <div className={s.formCardHeader}>
          <div className={s.formCardIcon} style={{ background: "#eff6ff", color: "#2563eb" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
          </div>
          <span className={s.formCardTitle}>Flight Setup</span>
        </div>
        <div className={s.configGrid}>
          <div className={s.configCard}>
            <div className={s.configCardLabel}>Pricing Mode</div>
            <div className={s.flightRadioCol}>
              {[{ key: "FULL", label: "Full Flight", desc: "Single price for entire flight" }, { key: "SPLIT", label: "Split Flight", desc: "Separate price per direction" }].map((pm) => (
                <label key={pm.key} className={`${s.flightRadioCard} ${form.flightPricingMode === pm.key ? s.flightRadioCardActive : ""}`}>
                  <input type="radio" name="flightPricingMode" checked={form.flightPricingMode === pm.key} onChange={() => handlePricingModeChange(pm.key)} className={s.flightRadioInput} />
                  <div><div className={s.flightRadioLabel}>{pm.label}</div><div className={s.flightRadioDesc}>{pm.desc}</div></div>
                </label>
              ))}
            </div>
          </div>
          <div className={s.configCard}>
            <div className={s.configCardLabel}>Flight Type</div>
            <div className={s.flightRadioCol}>
              {[{ key: "OW", label: "One Way", desc: "Single direction only" }, { key: "RT", label: "Round Trip", desc: "Outbound + return" }, { key: "Multi", label: "Multi-City", desc: "Multiple custom legs" }].map((ft) => {
                const dis = ft.key === "OW" && isSplit;
                return (
                  <label key={ft.key} className={`${s.flightRadioCard} ${form.flightType === ft.key ? s.flightRadioCardActive : ""} ${dis ? s.flightRadioCardDisabled : ""}`}>
                    <input type="radio" name="flightType" checked={form.flightType === ft.key} disabled={dis} onChange={() => handleFlightTypeChange(ft.key)} className={s.flightRadioInput} />
                    <div><div className={s.flightRadioLabel}>{ft.label}</div><div className={s.flightRadioDesc}>{dis ? "N/A with Split pricing" : ft.desc}</div></div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div className={s.mfRow2}>
          <div className={s.field}>
            <label className={s.fieldLabel}>Booking Context</label>
            {hasOtherProducts ? (
              <div className={`${s.flightModeBtn} ${s.flightModeBtnActive}`} style={{ alignSelf: "flex-start", cursor: "default" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                Flight within Travel Booking
              </div>
            ) : (
              <div className={s.flightModeToggle}>
                {[{ key: "flightonly", label: "Flight Only" }, { key: "within_travel", label: "Flight within Travel Booking" }].map((m) => (
                  <button key={m.key} type="button" className={`${s.flightModeBtn} ${form.flightBookingMode === m.key ? s.flightModeBtnActive : ""}`} onClick={() => f("flightBookingMode", m.key)}>
                    {form.flightBookingMode === m.key && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* ── SUPPLIER & SERVICE ── */}
      <div className={s.formCard} style={!form.flightPricingMode || !form.flightType ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
        <div className={s.formCardHeader}>
          <div className={s.formCardIcon} style={{ background: "#fef3c7", color: "#d97706" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <span className={s.formCardTitle}>Supplier & Service</span>
        </div>
        {(!form.flightPricingMode || !form.flightType) && (
          <div className={s.autofillNotice}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            Please complete Flight Setup first before filling supplier and service information.
          </div>
        )}
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Supplier <span className={s.req}>*</span></label><select className={s.input} value={form.supplierId || ""} onChange={(e) => { const id = e.target.value ? Number(e.target.value) : null; const sup = supplierOptions.find((x) => String(x.id) === e.target.value); f("supplierId", id); f("supplier", sup?.name || ""); f("supplierCommercialRole", sup ? resolveSupplierCommercialRole(sup, "Flight") : ""); }}><option value="">{loadingSuppliers ? "Loading suppliers..." : "Select Supplier"}</option>{supplierOptions.map((sup) => <option key={sup.id} value={sup.id}>{sup.name}{sup.type ? ` (${sup.type})` : ""}</option>)}</select>{form.supplierId && form.supplierCommercialRole && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 999, background: form.supplierCommercialRole === "MERCHANT" ? "#fef3c7" : "#dbeafe", color: form.supplierCommercialRole === "MERCHANT" ? "#92400e" : "#1e40af", marginTop: 4 }}>{form.supplierCommercialRole === "MERCHANT" ? "Merchant" : "Agent"}</span>}</div>
          <div className={s.field}><label className={s.fieldLabel}>Supplier Booking Reference</label><input className={s.input} placeholder="Enter reference" value={form.supplierRef} onChange={(e) => f("supplierRef", e.target.value.toUpperCase())} style={{ textTransform: "uppercase" }} /></div>
        </div>
        {supplierRefWarn && <DuplicateWarn data={supplierRefWarn} label="Supplier Reference" />}
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Creation Date</label><input className={s.input} type="date" value={form.creationDate || bookingDate || ""} onChange={(e) => f("creationDate", e.target.value)} /></div>
          <div className={s.field}><label className={s.fieldLabel}>Service Status</label><select className={s.input} value={form.status} onChange={(e) => f("status", e.target.value)}><option value="">Select Status</option>{PRODUCT_STATUSES.map((st) => <option key={st}>{st}</option>)}</select></div>
        </div>
      </div>

      {/* ── FLIGHT REFERENCE (Full pricing only) ── */}
      {!isSplit && (
        <div className={s.formCard}>
          <div className={s.formCardHeader}>
            <div className={s.formCardIcon} style={{ background: "#f0fdf4", color: "#16a34a" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
            </div>
            <span className={s.formCardTitle}>Flight Reference</span>
          </div>
          <div className={s.mfRow2}>
            <PnrInput value={form.airlinePnr} onChange={(e) => f("airlinePnr", e.target.value.toUpperCase())} required />
            <div className={s.field}><label className={s.fieldLabel}>Master Ticket Number</label><input className={s.input} placeholder="e.g. 176-2345678900" value={form.ticketNumber} onChange={(e) => f("ticketNumber", e.target.value)} /></div>
          </div>
          {pnrWarn.airlinePnr && <DuplicateWarn data={pnrWarn.airlinePnr} label="PNR" />}
        </div>
      )}

      {/* ── FLIGHT LEGS ── */}
      <div className={s.formCard}>
        <div className={s.formCardHeader}>
          <div className={s.formCardIcon} style={{ background: "#eef2ff", color: "#4f46e5" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>
          </div>
          <span className={s.formCardTitle}>Flight Legs</span>
        </div>

        {/* One Way */}
        {form.flightType === "OW" && (
          <>
            <div className={s.sectionLabel}><span className={s.sectionLabelDot} />One Way Flight<span className={s.segCount}>{outboundLegs.length}</span></div>
            <DirectionGroup direction="Outbound" title="One Way Flight" legs={outboundLegs} onUpdateLeg={updateLeg} onRemoveLeg={removeLeg} onAddLeg={addLeg} onCopyBaggage={copyBaggage} canAdd flightType="OW" pnrFields={null} globalTotalLegs={allLegs.length} {...sharedProps} />
          </>
        )}

        {/* Round Trip */}
        {form.flightType === "RT" && (
          <>
            <div className={s.sectionLabel}><span className={s.sectionLabelDot} />{isSplit ? "Split " : ""}Round Trip Flight<span className={s.segCount}>{allLegs.length} total</span></div>
            {rtFullAutofill && (
              <div className={s.autofillNotice}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                Inbound airports and airline are auto-filled from the outbound flight. Editing an inbound field stops auto-updates for that field.
              </div>
            )}
            <DirectionGroup direction="Outbound" title="Outbound Flight" legs={outboundLegs} onUpdateLeg={updateLeg} onRemoveLeg={removeLeg} onAddLeg={addLeg} onCopyBaggage={copyBaggage} canAdd flightType="RT" pnrFields={isSplit ? { pnr: form.outboundPnr || "", ticket: form.outboundTicket || "", onPnrChange: (v) => f("outboundPnr", v), onTicketChange: (v) => f("outboundTicket", v) } : null} globalTotalLegs={allLegs.length} {...sharedProps} />
            {isSplit && pnrWarn.outboundPnr && <DuplicateWarn data={pnrWarn.outboundPnr} label="Outbound PNR" />}
            <div className={s.directionDivider}><div className={s.directionDividerLine} /><span className={s.directionDividerLabel}>Return Flight</span><div className={s.directionDividerLine} /></div>
            <DirectionGroup direction="Inbound" title="Inbound Flight" legs={inboundLegs} onUpdateLeg={updateLeg} onRemoveLeg={removeLeg} onAddLeg={addLeg} onCopyBaggage={copyBaggage} autofillTarget={rtFullAutofill} canAdd flightType="RT" pnrFields={isSplit ? { pnr: form.inboundPnr || "", ticket: form.inboundTicket || "", onPnrChange: (v) => f("inboundPnr", v), onTicketChange: (v) => f("inboundTicket", v) } : null} globalTotalLegs={allLegs.length} {...sharedProps} />
            {isSplit && pnrWarn.inboundPnr && <DuplicateWarn data={pnrWarn.inboundPnr} label="Inbound PNR" />}
          </>
        )}

        {/* Multi-City */}
        {form.flightType === "Multi" && (
          <>
            <div className={s.sectionLabel}><span className={s.sectionLabelDot} />Multi-City Flight<span className={s.segCount}>{allLegs.length} total</span></div>
            <DirectionGroup direction="Outbound" title="Outbound Legs" legs={outboundLegs} onUpdateLeg={updateLeg} onRemoveLeg={removeLeg} onAddLeg={addLeg} onCopyBaggage={copyBaggage} canAdd flightType="Multi" pnrFields={isSplit ? { pnr: form.outboundPnr || "", ticket: form.outboundTicket || "", onPnrChange: (v) => f("outboundPnr", v), onTicketChange: (v) => f("outboundTicket", v) } : null} globalTotalLegs={allLegs.length} {...sharedProps} />
            <div className={s.directionDivider}><div className={s.directionDividerLine} /><span className={s.directionDividerLabel}>Return Legs</span><div className={s.directionDividerLine} /></div>
            <DirectionGroup direction="Inbound" title="Inbound Legs" legs={inboundLegs} onUpdateLeg={updateLeg} onRemoveLeg={removeLeg} onAddLeg={addLeg} onCopyBaggage={copyBaggage} canAdd flightType="Multi" pnrFields={isSplit ? { pnr: form.inboundPnr || "", ticket: form.inboundTicket || "", onPnrChange: (v) => f("inboundPnr", v), onTicketChange: (v) => f("inboundTicket", v) } : null} globalTotalLegs={allLegs.length} {...sharedProps} />
          </>
        )}
      </div>

      {/* ── INTERNAL NOTES ── */}
      <div className={s.formCard}>
        <div className={s.formCardHeader}>
          <div className={s.formCardIcon} style={{ background: "#f5f3ff", color: "#7c3aed" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          </div>
          <span className={s.formCardTitle}>Internal Notes</span>
        </div>
        <textarea className={`${s.input} ${s.textarea}`} placeholder="Add internal notes about this flight..." rows={3} value={form.notes} onChange={(e) => f("notes", e.target.value)} />
      </div>

      {toast && (
        <div className={s.flightToast} role="status" aria-live="polite">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          {toast}
        </div>
      )}
    </>
  );
};

export default FlightForm;
